import { useTranslation } from "react-i18next";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { BCPXmark } from "../../../components/icons/bcp/BCPIcons";
import { Formik } from "formik";
import { postNotification } from "../../../components/utilities/Notifications";
import { handlePagingAll } from "../../../components/api/APIUtilities";
import * as Yup from "yup";
import { postData } from "../../../components/utilities/UtilityFunctions";
import ScorecardPicker from "../../../components/utilities/ScorecardPicker";
import {
  flatternFormSchema,
  getEligibleFields,
  playerFieldKey,
  updateFormulas,
} from "../../../components/utilities/FormUtilities";

// BCP-416: given the new scorecard's field, guess which OLD field it should preserve data
// from, by matching label text - but only within the same player association when one is
// determinable, so e.g. a new "p2-gamePoints" defaults to the old p2 field, not p1's, even
// though they'd otherwise share the identical "Victory Points" label. Falls back to any
// same-label field, then to no default (the TO must pick manually).
function guessDefaultMapping(newField, oldFields) {
  const newLabel = (newField.label || "").trim().toLowerCase();
  if (!newLabel) return "";

  const sameLabel = oldFields.filter(
    (old) => (old.label || "").trim().toLowerCase() === newLabel
  );
  if (!sameLabel.length) return "";

  const newParsed = playerFieldKey(newField.id);
  if (newParsed) {
    const samePlayer = sameLabel.find(
      (old) => playerFieldKey(old.id)?.playerNum === newParsed.playerNum
    );
    if (samePlayer) return samePlayer.id;
  }

  return sameLabel[0].id;
}

// A field's label alone can be ambiguous (both players often share the same label, e.g.
// "Victory Points") - append which player it belongs to when we can tell, so the dropdown
// options are actually distinguishable.
function describeField(col) {
  if (!col.label) return col.id;
  const parsed = playerFieldKey(col.id);
  return parsed ? `${col.label} (Player ${parsed.playerNum})` : col.label;
}

export default function SwapScorecardModal(props) {
  const { t } = useTranslation();
  const {
    sCMenuOpen,
    setSCMenuOpen,
    authContext,
    event,
    pairing,
    // Only provided when this modal is opened from Pairing.js (BCP-416 scope) - TeamPairing.js
    // doesn't pass these, so it keeps today's Clear-only behavior unchanged.
    values,
    schema,
    setValues,
    setValuesDirty,
    setScorecard,
    setSchema,
  } = props;

  const supportsPreserve = Boolean(
    values && schema && setValues && setValuesDirty && setScorecard && setSchema
  );

  const [scorecards, setScorecards] = useState(null);
  const [teamScorecards, setTeamScorecards] = useState(null);
  const [scorecardDict, setScorecardDict] = useState({});

  // Preserve-mapping step state
  const [mapping, setMapping] = useState(null); // { previewScorecard, previewSchema, fieldMapping }
  const [acknowledgedDataLoss, setAcknowledgedDataLoss] = useState(false);

  useEffect(() => {
    const fetchScorecards = async (gsystem) => {
      let filters = {
        gameSystemId: gsystem?.id,
      };
      if (authContext?.authState?.identity?.userId) {
        filters.ownerId = authContext?.authState?.identity?.userId;
      } else {
        filters.public = true;
      }
      if (gsystem?.boardGameEvent) {
        filters.type = "SeatedPairing";
      }

      try {
        const apiScorecards = await handlePagingAll("/scorecards", filters);

        let apiScorecardDict = {};

        let foundCurrent = false;
        let scorecardArray = [];

        let foundTeamCurrent = false;
        let teamScorecardArray = [];

        apiScorecards.forEach((scorecard) => {
          apiScorecardDict[scorecard.id] = scorecard;
          if (event) {
            if (event?.scorecardId === scorecard.id) {
              foundCurrent = true;
            }
            if (event?.teamScorecardId === scorecard.id) {
              foundTeamCurrent = true;
            }
          }

          if (
            scorecard.type === "Pairing" ||
            scorecard.type === "SeatedPairing"
          ) {
            scorecardArray.push({
              value: scorecard.id,
              label: scorecard.name,
              default: gsystem?.scorecardId === scorecard.id,
              current: event?.scorecardId === scorecard.id,
              scorecardType: scorecard.scorecardType,
            });
          } else if (scorecard.type === "TeamPairing") {
            teamScorecardArray.push({
              value: scorecard.id,
              label: scorecard.name,
              default: gsystem?.teamScorecardId === scorecard.id,
              current: event?.teamScorecardId === scorecard.id,
              scorecardType: scorecard.scorecardType,
            });
          }
        });

        if (
          !foundCurrent &&
          event?.scorecardId &&
          event?.scorecardId !== gsystem?.scorecardId
        ) {
          scorecardArray.push({
            value: event?.scorecardId,
            label: event?.scorecard?.name || event?.name,
            current: true,
            scorecardType: event?.scorecard?.scorecardType,
          });
        }

        if (
          !foundTeamCurrent &&
          event?.teamScorecardId &&
          event?.teamScorecardId !== gsystem?.teamScorecardId
        ) {
          teamScorecardArray.push({
            value: event?.teamScorecardId,
            label: event?.teamScorecard?.name || event?.name,
            current: true,
            scorecardType: event?.teamScorecard?.scorecardType,
          });
        }

        scorecardArray.sort((a, b) => {
          if (a.default) return -1;
          if (b.default) return 1;
          if (a.current) return -1;
          if (b.current) return 1;
          var nameA = a.label.toLowerCase();
          var nameB = b.label.toLowerCase();
          if (nameA > nameB) return 1;
          if (nameA < nameB) return -1;
          return 0;
        });
        setScorecards(scorecardArray);

        teamScorecardArray.sort((a, b) => {
          if (a.default) return -1;
          if (b.default) return 1;
          if (a.current) return -1;
          if (b.current) return 1;
          var nameA = a.label.toLowerCase();
          var nameB = b.label.toLowerCase();
          if (nameA > nameB) return 1;
          if (nameA < nameB) return -1;
          return 0;
        });
        setTeamScorecards(teamScorecardArray);

        setScorecardDict(apiScorecardDict);
      } catch (err) {
        postNotification("error", err);
      }
    };

    fetchScorecards(event?.gameSystem);
    // eslint-disable-next-line
  }, []);

  async function swapScorecard(id) {
    try {
      const apiScorecard = await postData(
        `/pairings/${pairing?.id}/swapScorecard`,
        {
          scorecardId: id,
          pairingType: pairing?.pairingType,
        }
      );

      if (apiScorecard) {
        postNotification(
          "success",
          t("notifications.success.scorecardChangedSuccessfully")
        );
        setTimeout(() => {
          window.location.reload();
        }, [1500]);
      }
    } catch (err) {
      postNotification("error", err);
    }
  }

  function closeAndReset() {
    setSCMenuOpen(false);
    setMapping(null);
    setAcknowledgedDataLoss(false);
  }

  // Builds the preserve-mapping step: fetches the new scorecard's schema, lists its eligible
  // fields alongside the current scorecard's eligible fields, and seeds a best-guess mapping.
  async function startPreserve(newScorecardId) {
    const newScorecard = scorecardDict[newScorecardId];
    if (!newScorecard) return;

    try {
      const previewSchema = await flatternFormSchema(newScorecard, pairing);
      const oldFields = getEligibleFields(schema);
      const newFields = getEligibleFields(previewSchema);

      const fieldMapping = {};
      newFields.forEach((newField) => {
        fieldMapping[newField.id] = guessDefaultMapping(newField, oldFields);
      });

      setMapping({
        previewScorecard: newScorecard,
        previewSchema,
        oldFields,
        newFields,
        fieldMapping,
      });
      setAcknowledgedDataLoss(false);
    } catch (err) {
      postNotification("error", err);
    }
  }

  function applyPreserve() {
    const { previewScorecard, previewSchema, fieldMapping } = mapping;

    const mergedValues = { ...previewSchema.initialValues };
    Object.entries(fieldMapping).forEach(([newFieldId, oldFieldId]) => {
      if (oldFieldId) {
        mergedValues[newFieldId] = values[oldFieldId];
      }
    });

    const recalculated = updateFormulas(previewSchema.formulas, mergedValues);

    // BCPScorecardForm has its own load effect (deps: [schema, record]) that resets `values`
    // to `schema.initialValues` whenever the schema changes - which fires right after setSchema
    // below. Update the schema's own initialValues in place so that reset lands on our
    // preserved/recalculated data instead of clobbering it back to blank defaults.
    previewSchema.initialValues = recalculated;

    setScorecard(previewScorecard);
    setSchema(previewSchema);
    setValues(recalculated);
    setValuesDirty(true);

    postNotification(
      "success",
      "Scorecard changed on this pairing. Scores from mapped fields were preserved - " +
        "submit the scorecard to save this change."
    );
    closeAndReset();
  }

  const unmappedOldFields = mapping
    ? mapping.oldFields.filter(
        (old) => !Object.values(mapping.fieldMapping).includes(old.id)
      )
    : [];

  return (
    <Modal
      disableAutoFocus
      open={Boolean(sCMenuOpen)}
      onClose={closeAndReset}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
      sx={{ zIndex: 9999 }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: ["95vw", "85vw", 600],
          maxHeight: "95vh",
          overflowY: "auto",
          bgcolor: "modal.background",
          color: "modal.text",
          border: "1px solid rgba(0, 0, 0, 0.12)",
          borderRadius: 1,
          boxShadow: 24,
          p: 4,
          textAlign: "center",
        }}
      >
        <IconButton
          aria-label="cancel"
          color="error"
          sx={{ position: "absolute", top: "4px", right: "4px" }}
          onClick={closeAndReset}
        >
          <BCPXmark />
        </IconButton>

        {mapping ? (
          <Grid container spacing={2} sx={{ textAlign: "left" }}>
            <Grid item xs={12} sx={{ textAlign: "center" }}>
              <Typography variant="h4" component="h2">
                Preserve Scores
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose which field on the new scorecard (
                {mapping.previewScorecard.name}) each existing value should
                carry over to. Fields left as "None" will use the new
                scorecard's default instead.
              </Typography>
            </Grid>

            {mapping.newFields.map((newField) => (
              <Grid item xs={12} key={newField.id}>
                <FormControl fullWidth size="small">
                  <InputLabel>{describeField(newField)}</InputLabel>
                  <Select
                    label={describeField(newField)}
                    value={mapping.fieldMapping[newField.id] || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setMapping((prev) => ({
                        ...prev,
                        fieldMapping: {
                          ...prev.fieldMapping,
                          [newField.id]: value,
                        },
                      }));
                    }}
                  >
                    <MenuItem value="">
                      <em>None - use new scorecard's default</em>
                    </MenuItem>
                    {mapping.oldFields.map((oldField) => (
                      <MenuItem key={oldField.id} value={oldField.id}>
                        {describeField(oldField)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ))}

            {unmappedOldFields.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  These existing values are not mapped to anything on the new
                  scorecard and will be lost:{" "}
                  {unmappedOldFields.map((f) => describeField(f)).join(", ")}
                </Alert>
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={
                    <Checkbox
                      checked={acknowledgedDataLoss}
                      onChange={(e) =>
                        setAcknowledgedDataLoss(e.target.checked)
                      }
                    />
                  }
                  label="I understand the unmapped values listed above will be lost."
                />
              </Grid>
            )}

            <Grid item xs={6}>
              <Button
                color="error"
                fullWidth
                variant="outlined"
                onClick={() => setMapping(null)}
              >
                Back
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                color="success"
                fullWidth
                variant="contained"
                disabled={unmappedOldFields.length > 0 && !acknowledgedDataLoss}
                onClick={applyPreserve}
              >
                Apply
              </Button>
            </Grid>
          </Grid>
        ) : (
          <Formik
            enableReinitialize={true}
            initialValues={{
              eventType: event?.doublesEvent
                ? "Doubles Event"
                : event?.teamEvent
                  ? "Teams Event"
                  : "Singles Event",
              pairingType: pairing?.pairingType || "",
              scorecardId:
                pairing?.scorecardId ||
                pairing?.scorecard?.id ||
                event?.scorecardId ||
                event?.gameSystem?.scorecardId ||
                "",
              teamScorecardId:
                event?.teamScorecardId ||
                event?.gameSystem?.teamScorecardId ||
                "",
              matchWinsForTie:
                event?.matchWinsForTie !== undefined ? event.matchWinsForTie : 0,
              marginOfVictoryForTie:
                event?.marginOfVictoryForTie !== undefined
                  ? event.marginOfVictoryForTie
                  : 0,
            }}
            validationSchema={Yup.object().shape({
              scorecardId: Yup.string().required("Required"),
              teamScorecardId: event?.teamEvent
                ? Yup.string().required("Required")
                : null,
            })}
            onSubmit={async (values) => {
              if (values.pairingType !== "TeamPairing") {
                delete values.teamScorecardId;
              }

              if (values.pairingType === "TeamPairing" && !event?.doublesEvent) {
                delete values.scorecardId;
              }

              if (
                values?.scorecardId &&
                values?.scorecardId !== pairing?.scorecard?.id
              ) {
                await swapScorecard(values?.scorecardId);
              } else if (
                values?.teamScorecardId &&
                values?.teamScorecardId !== pairing?.scorecard?.id
              ) {
                await swapScorecard(values?.teamScorecardId);
              }
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              setFieldValue,
              touched,
              values,
            }) => {
              const hasChanged =
                values?.pairingType === "TeamPairing" &&
                values?.teamScorecardId &&
                values?.teamScorecardId !== pairing?.scorecard?.id
                  ? true
                  : (values?.pairingType !== "TeamPairing" ||
                        (values?.pairingType === "TeamPairing" &&
                          event?.doublesEvent)) &&
                      values?.scorecardId &&
                      values?.scorecardId !== pairing?.scorecard?.id
                    ? true
                    : false;

              const selectedScorecardId =
                values?.pairingType === "TeamPairing" && !event?.doublesEvent
                  ? values?.teamScorecardId
                  : values?.scorecardId;

              return (
                <form onSubmit={handleSubmit}>
                  <Grid
                    container
                    spacing={2}
                    sx={{
                      pb: 1,
                      mb: 2,
                      borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                    }}
                  >
                    <Grid item xs={12}>
                      <Typography variant="h4" component="h2">
                        Change Scorecards
                      </Typography>
                    </Grid>

                    {values?.pairingType === "TeamPairing" &&
                      !event?.doublesEvent && (
                        <>
                          <Grid item xs={12}>
                            <ScorecardPicker
                              name="teamScorecardId"
                              label="Team Scorecard"
                              value={values.teamScorecardId}
                              handleChange={(e) => {
                                if (
                                  scorecardDict?.[e.target.value]?.variables
                                    ?.length > 0
                                ) {
                                  let variables =
                                    scorecardDict[e.target.value].variables;
                                  for (let variable of variables) {
                                    if (values[variable.id] === undefined) {
                                      setFieldValue(
                                        variable.id,
                                        variable.default !== undefined
                                          ? variable.default
                                          : 0
                                      );
                                    }
                                  }
                                }

                                handleChange(e);
                              }}
                              scorecards={teamScorecards}
                              inclAny={false}
                              error={Boolean(
                                touched.teamScorecardId &&
                                  errors.teamScorecardId
                              )}
                              helperText={
                                touched.teamScorecardId &&
                                errors.teamScorecardId
                              }
                            />
                          </Grid>
                        </>
                      )}

                    {(values?.pairingType === "Pairing" ||
                      values?.pairingType === "SeatedPairing" ||
                      (values?.pairingType === "TeamPairing" &&
                        event?.doublesEvent)) && (
                      <Grid item xs={12}>
                        <ScorecardPicker
                          name="scorecardId"
                          label="Scorecard"
                          value={values.scorecardId}
                          handleChange={handleChange}
                          scorecards={scorecards}
                          inclAny={false}
                          error={Boolean(
                            touched.scorecardId && errors.scorecardId
                          )}
                          helperText={touched.scorecardId && errors.scorecardId}
                        />
                      </Grid>
                    )}

                    {supportsPreserve && hasChanged ? (
                      <>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="body2" color="text.secondary">
                            Clear resets all scores on this pairing. Preserve
                            lets you choose which existing values carry over
                            to the new scorecard's fields.
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Button
                            color="error"
                            fullWidth
                            variant="outlined"
                            onClick={() => {
                              setSCMenuOpen(false);
                            }}
                          >
                            Cancel
                          </Button>
                        </Grid>
                        <Grid item xs={6}>
                          <Button
                            color="warning"
                            fullWidth
                            variant="outlined"
                            onClick={handleSubmit}
                          >
                            Clear Scores
                          </Button>
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            color="success"
                            fullWidth
                            variant="contained"
                            onClick={() => startPreserve(selectedScorecardId)}
                          >
                            Preserve Scores
                          </Button>
                        </Grid>
                      </>
                    ) : (
                      <>
                        <Grid item xs={6}>
                          <Button
                            color="error"
                            fullWidth
                            variant="outlined"
                            onClick={() => {
                              setSCMenuOpen(false);
                            }}
                          >
                            Cancel
                          </Button>
                        </Grid>
                        <Grid item xs={6}>
                          <Button
                            color="success"
                            fullWidth
                            variant="contained"
                            type="submit"
                            disabled={!hasChanged}
                          >
                            Confirm
                          </Button>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </form>
              );
            }}
          </Formik>
        )}
      </Box>
    </Modal>
  );
}
